import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  login, getToken, clearToken, fetchContent, updateContent,
  listTeam, createMember, updateMember, deleteMember,
  listLeads, deleteLead, uploadFile, resolveAdminImg,
} from "../lib/adminApi";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const Input = (props) => (
  <input {...props} className={`w-full bg-white border border-black/15 focus:border-black rounded-lg px-3 py-2 text-sm outline-none ${props.className || ""}`} />
);
const Textarea = (props) => (
  <textarea {...props} className={`w-full bg-white border border-black/15 focus:border-black rounded-lg px-3 py-2 text-sm outline-none ${props.className || ""}`} />
);
const Btn = ({ variant = "primary", ...props }) => {
  const base = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-colors disabled:opacity-50";
  const styles = variant === "primary"
    ? "bg-black text-white hover:bg-zinc-800"
    : variant === "ghost"
    ? "bg-white text-black border border-black/15 hover:border-black"
    : "bg-red-600 text-white hover:bg-red-700";
  return <button {...props} className={`${base} ${styles} ${props.className || ""}`} />;
};

const ImageUploader = ({ value, onChange }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadFile(f);
      onChange(url);
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        {value ? (
          <img src={resolveAdminImg(value)} alt="" className="w-16 h-16 object-cover rounded-lg border border-black/10" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-zinc-100 border border-black/10" />
        )}
        <input type="file" accept="image/*,video/*" ref={ref} onChange={handle} className="hidden" />
        <Btn variant="ghost" type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="upload-button">
          {busy ? "Uploading…" : "Upload file"}
        </Btn>
        {value && <Btn variant="danger" type="button" onClick={() => onChange("")}>Remove</Btn>}
      </div>
      <Input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" />
    </div>
  );
};

// ----- Tabs -----

const HeroAboutTab = ({ content, setContent }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <Field label="Hero eyebrow"><Input value={content.hero_eyebrow || ""} onChange={(e) => setContent({ ...content, hero_eyebrow: e.target.value })} /></Field>
    <div />
    <Field label="Hero title — line 1"><Input value={content.hero_title_line1 || ""} onChange={(e) => setContent({ ...content, hero_title_line1: e.target.value })} /></Field>
    <Field label="Hero title — line 2"><Input value={content.hero_title_line2 || ""} onChange={(e) => setContent({ ...content, hero_title_line2: e.target.value })} /></Field>
    <Field label="Hero tagline"><Textarea rows={3} value={content.hero_tagline || ""} onChange={(e) => setContent({ ...content, hero_tagline: e.target.value })} /></Field>
    <div className="md:row-span-2">
      <Field label="Hero photo / video">
        <ImageUploader value={content.hero_image} onChange={(v) => setContent({ ...content, hero_image: v })} />
      </Field>
    </div>
    <Field label="Hero photo — caption label"><Input value={content.hero_photo_caption_label || ""} onChange={(e) => setContent({ ...content, hero_photo_caption_label: e.target.value })} /></Field>
    <Field label="Hero photo — caption title"><Input value={content.hero_photo_caption_title || ""} onChange={(e) => setContent({ ...content, hero_photo_caption_title: e.target.value })} /></Field>

    <div className="md:col-span-2">
      <label className="inline-flex items-center gap-3 cursor-pointer select-none">
        <input type="checkbox" checked={!!content.hero_grayscale}
          onChange={(e) => setContent({ ...content, hero_grayscale: e.target.checked })}
          className="w-4 h-4 accent-black" data-testid="hero-grayscale-toggle" />
        <span className="text-sm text-zinc-700">Display hero photo in black &amp; white</span>
      </label>
    </div>

    <div className="md:col-span-2 border-t border-black/10 pt-6 mt-2">
      <div className="text-xs uppercase tracking-[0.28em] text-zinc-500 mb-4">About / Mission</div>
    </div>
    <Field label="Mission eyebrow"><Input value={content.mission_eyebrow || ""} onChange={(e) => setContent({ ...content, mission_eyebrow: e.target.value })} /></Field>
    <div />
    <Field label="Mission headline"><Textarea rows={3} value={content.mission_headline || ""} onChange={(e) => setContent({ ...content, mission_headline: e.target.value })} /></Field>
    <Field label="Mission body"><Textarea rows={5} value={content.mission_body || ""} onChange={(e) => setContent({ ...content, mission_body: e.target.value })} /></Field>

    <div className="md:col-span-2 border-t border-black/10 pt-6 mt-2">
      <div className="text-xs uppercase tracking-[0.28em] text-zinc-500 mb-4">Contact info</div>
    </div>
    <Field label="Phone"><Input value={content.contact_phone || ""} onChange={(e) => setContent({ ...content, contact_phone: e.target.value })} /></Field>
    <Field label="Address"><Input value={content.contact_address || ""} onChange={(e) => setContent({ ...content, contact_address: e.target.value })} /></Field>
    <div className="md:col-span-2">
      <Field label="Notification email — leads from contact form will be sent here">
        <Input type="email" value={content.notification_email || ""} onChange={(e) => setContent({ ...content, notification_email: e.target.value })} placeholder="believeteammy@gmail.com" data-testid="notification-email-input" />
      </Field>
    </div>
  </div>
);

const ListEditor = ({ items, setItems, fields, label, testId }) => (
  <div className="space-y-4" data-testid={testId}>
    {items.map((it, i) => (
      <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start bg-zinc-50 border border-black/5 rounded-2xl p-4">
        {fields.map((f) => (
          <div key={f.key} className={`md:col-span-${f.span || 6}`}>
            <Field label={f.label}>
              {f.textarea
                ? <Textarea rows={2} value={it[f.key] || ""} onChange={(e) => { const c = [...items]; c[i] = { ...c[i], [f.key]: e.target.value }; setItems(c); }} />
                : <Input value={it[f.key] || ""} onChange={(e) => { const c = [...items]; c[i] = { ...c[i], [f.key]: e.target.value }; setItems(c); }} />}
            </Field>
          </div>
        ))}
        <div className="md:col-span-12 flex justify-end">
          <Btn variant="danger" type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))}>Delete</Btn>
        </div>
      </div>
    ))}
    <Btn variant="ghost" type="button" onClick={() => setItems([...items, fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {})])}>+ Add {label}</Btn>
  </div>
);

const StatsTab = ({ content, setContent }) => (
  <ListEditor
    items={content.stats || []} setItems={(v) => setContent({ ...content, stats: v })} label="stat" testId="stats-editor"
    fields={[{ key: "value", label: "Value (e.g. 21+)", span: 4 }, { key: "label", label: "Label", span: 8 }]}
  />
);
const MilestonesTab = ({ content, setContent }) => (
  <ListEditor
    items={content.milestones || []} setItems={(v) => setContent({ ...content, milestones: v })} label="milestone" testId="milestones-editor"
    fields={[{ key: "year", label: "Year", span: 3 }, { key: "title", label: "Event", span: 9 }]}
  />
);
const ServicesTab = ({ content, setContent }) => (
  <ListEditor
    items={content.services || []} setItems={(v) => setContent({ ...content, services: v })} label="service" testId="services-editor"
    fields={[{ key: "title", label: "Title", span: 4 }, { key: "desc", label: "Description", span: 8, textarea: true }]}
  />
);
const PressTab = ({ content, setContent }) => (
  <div className="space-y-3" data-testid="press-editor">
    {(content.press || []).map((p, i) => (
      <div key={i} className="flex items-center gap-3">
        <Input value={p} onChange={(e) => { const c = [...content.press]; c[i] = e.target.value; setContent({ ...content, press: c }); }} />
        <Btn variant="danger" type="button" onClick={() => setContent({ ...content, press: content.press.filter((_, idx) => idx !== i) })}>×</Btn>
      </div>
    ))}
    <Btn variant="ghost" type="button" onClick={() => setContent({ ...content, press: [...(content.press || []), "NEW PRESS"] })}>+ Add press</Btn>
  </div>
);

const TeamTab = ({ token, onChange }) => {
  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = { name: "", title: "", img: "", achievements: [""], order: 0, grayscale: false };

  const reload = async () => setTeam(await listTeam());
  useEffect(() => { reload(); }, []);

  const save = async () => {
    const payload = { ...editing, achievements: (editing.achievements || []).filter(Boolean), order: Number(editing.order) || 0 };
    if (editing.id) await updateMember(editing.id, payload);
    else await createMember(payload);
    setEditing(null);
    await reload();
    onChange?.();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this team member?")) return;
    await deleteMember(id); await reload(); onChange?.();
  };

  return (
    <div className="space-y-6" data-testid="team-editor">
      <div className="flex justify-end">
        <Btn type="button" onClick={() => setEditing(blank)} data-testid="add-member-btn">+ Add team member</Btn>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {team.map((m) => (
          <div key={m.id} className="bg-white border border-black/10 rounded-2xl p-4 flex gap-4">
            {m.img && <img src={resolveAdminImg(m.img)} alt="" className="w-20 h-24 rounded-lg object-cover" />}
            <div className="flex-1">
              <div className="font-display text-lg">{m.name}</div>
              <div className="text-xs text-zinc-500">{m.title}</div>
              <div className="text-[11px] text-zinc-500 mt-1">Order: {m.order}</div>
              <div className="mt-3 flex gap-2">
                <Btn variant="ghost" type="button" onClick={() => setEditing(m)}>Edit</Btn>
                <Btn variant="danger" type="button" onClick={() => remove(m.id)}>Delete</Btn>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-auto p-8">
            <div className="font-display text-2xl mb-6">{editing.id ? "Edit" : "Add"} team member</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
              <Field label="Title"><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
              <Field label="Order (display position)"><Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></Field>
              <div className="md:col-span-2"><Field label="Photo">
                <ImageUploader value={editing.img} onChange={(v) => setEditing({ ...editing, img: v })} />
              </Field></div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <input type="checkbox" checked={!!editing.grayscale}
                    onChange={(e) => setEditing({ ...editing, grayscale: e.target.checked })}
                    className="w-4 h-4 accent-black" data-testid="member-grayscale-toggle" />
                  <span className="text-sm text-zinc-700">Display this photo in black &amp; white</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <Field label="Achievements">
                  <div className="space-y-2">
                    {(editing.achievements || []).map((a, i) => (
                      <div key={i} className="flex gap-2">
                        <Input value={a} onChange={(e) => { const c = [...editing.achievements]; c[i] = e.target.value; setEditing({ ...editing, achievements: c }); }} />
                        <Btn variant="danger" type="button" onClick={() => setEditing({ ...editing, achievements: editing.achievements.filter((_, idx) => idx !== i) })}>×</Btn>
                      </div>
                    ))}
                    <Btn variant="ghost" type="button" onClick={() => setEditing({ ...editing, achievements: [...(editing.achievements || []), ""] })}>+ Add achievement</Btn>
                  </div>
                </Field>
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <Btn variant="ghost" type="button" onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn type="button" onClick={save} data-testid="save-member-btn">Save</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const reload = async () => setLeads(await listLeads());
  useEffect(() => { reload(); }, []);
  return (
    <div className="space-y-3" data-testid="leads-list">
      {leads.length === 0 && <div className="text-sm text-zinc-500">No leads yet.</div>}
      {leads.map((l) => (
        <div key={l.id} className="bg-white border border-black/10 rounded-2xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-display text-lg">{l.name}</div>
              <div className="text-xs text-zinc-500">{l.email} · {new Date(l.created_at).toLocaleString()}</div>
              {l.message && <p className="text-sm text-zinc-700 mt-3 whitespace-pre-wrap">{l.message}</p>}
            </div>
            <Btn variant="danger" type="button" onClick={async () => { await deleteLead(l.id); reload(); }}>Delete</Btn>
          </div>
        </div>
      ))}
    </div>
  );
};

const GalleryTab = ({ content, setContent }) => {
  const items = content.gallery || [];
  const limit = 10;
  const update = (i, patch) => {
    const c = [...items]; c[i] = { ...c[i], ...patch }; setContent({ ...content, gallery: c });
  };
  const remove = (i) => setContent({ ...content, gallery: items.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; setContent({ ...content, gallery: c });
  };
  const add = () => {
    if (items.length >= limit) return;
    setContent({ ...content, gallery: [...items, { url: "", caption: "", kind: "image" }] });
  };
  return (
    <div className="space-y-4" data-testid="gallery-editor">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-500">{items.length} / {limit} items · drag-free reorder via arrows · supports images &amp; videos</div>
        <Btn type="button" onClick={add} disabled={items.length >= limit}>+ Add media</Btn>
      </div>
      {items.length === 0 && (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-black/5 rounded-2xl p-8 text-center">No media yet. Click "Add media" to start your gallery.</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it, i) => {
          const kind = it.kind || ((it.url || "").match(/\.(mp4|webm|mov)$/i) ? "video" : "image");
          return (
            <div key={i} className="bg-zinc-50 border border-black/5 rounded-2xl p-4 flex gap-4" data-testid={`gallery-item-${i}`}>
              <div className="w-28 h-20 rounded-lg bg-zinc-200 overflow-hidden flex-none">
                {it.url ? (
                  kind === "video" ? (
                    <video src={resolveAdminImg(it.url)} muted className="w-full h-full object-cover" />
                  ) : (
                    <img src={resolveAdminImg(it.url)} alt="" className="w-full h-full object-cover" />
                  )
                ) : null}
              </div>
              <div className="flex-1 space-y-2">
                <ImageUploader value={it.url} onChange={(v) => {
                  const detected = (v || "").match(/\.(mp4|webm|mov)$/i) ? "video" : "image";
                  update(i, { url: v, kind: detected });
                }} />
                <Input value={it.caption || ""} onChange={(e) => update(i, { caption: e.target.value })} placeholder="Optional caption" />
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <Btn variant="ghost" type="button" onClick={() => move(i, -1)} disabled={i === 0}>↑</Btn>
                    <Btn variant="ghost" type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1}>↓</Btn>
                  </div>
                  <Btn variant="danger" type="button" onClick={() => remove(i)}>Delete</Btn>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ----- Main -----

const TABS = [
  { id: "hero", label: "Hero & About" },
  { id: "team", label: "Team" },
  { id: "stats", label: "Stats" },
  { id: "milestones", label: "Milestones" },
  { id: "gallery", label: "Gallery" },
  { id: "services", label: "Services" },
  { id: "press", label: "Press" },
  { id: "leads", label: "Leads" },
];

const Admin = () => {
  const nav = useNavigate();
  const [authed, setAuthed] = useState(!!getToken());
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    const { content } = await fetchContent();
    setContent(content);
  };

  useEffect(() => { if (authed) load(); }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");
    try { await login(pw); setAuthed(true); }
    catch { setErr("Incorrect password"); }
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await updateContent(content);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="admin-login">
        <form onSubmit={handleLogin} className="w-full max-w-sm">
          <div className="font-display text-3xl tracking-tight">Admin</div>
          <p className="text-sm text-zinc-500 mt-1">BELIEVE TEAM control panel</p>
          <Field label="Password">
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="admin-password-input" />
          </Field>
          {err && <div className="text-xs text-red-600 mt-3" data-testid="admin-login-error">{err}</div>}
          <div className="mt-6 flex items-center gap-3">
            <Btn type="submit" data-testid="admin-login-submit">Enter</Btn>
            <button type="button" onClick={() => nav("/")} className="text-xs text-zinc-500 hover:text-black">← Back to site</button>
          </div>
        </form>
      </div>
    );
  }

  if (!content) return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display tracking-[0.28em] text-sm">BELIEVE TEAM — ADMIN</div>
          <div className="flex items-center gap-3">
            {tab !== "team" && tab !== "leads" && (
              <Btn type="button" onClick={save} disabled={saving} data-testid="save-content-btn">
                {saving ? "Saving…" : saved ? "Saved ✓" : "Save changes"}
              </Btn>
            )}
            <Btn variant="ghost" type="button" onClick={() => nav("/")}>View site</Btn>
            <Btn variant="ghost" type="button" onClick={() => { clearToken(); setAuthed(false); }} data-testid="admin-logout">Logout</Btn>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-3 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tab === t.id ? "bg-black text-white border-black" : "bg-white text-zinc-700 border-black/15 hover:border-black"}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === "hero" && <HeroAboutTab content={content} setContent={setContent} />}
        {tab === "team" && <TeamTab />}
        {tab === "stats" && <StatsTab content={content} setContent={setContent} />}
        {tab === "milestones" && <MilestonesTab content={content} setContent={setContent} />}
        {tab === "gallery" && <GalleryTab content={content} setContent={setContent} />}
        {tab === "services" && <ServicesTab content={content} setContent={setContent} />}
        {tab === "press" && <PressTab content={content} setContent={setContent} />}
        {tab === "leads" && <LeadsTab />}
      </main>
    </div>
  );
};

export default Admin;
